import { useEffect, useRef } from 'react';
import { isNotNil } from 'shared/src/utils';
import { useLogAnalyticsEvent } from 'shared/src/hooks/useLogAnalyticsEvent';
import type { ScreensReadModel } from 'shared/src/api';
import type { LogAnalyticsEventParams } from 'shared/src/utils/bridge/logAnalyticsEvent/types';

/**
 * 서버 응답 기반 VIEW 이벤트 로깅
 * iOS: MetaFeedReactor.trackViewLoggingEvent
 *   → 이벤트명: "VIEW" + "_" + screenName (e.g. VIEW_SEARCH_RESULT)
 *   → 채널: view_event_channels
 *   → 데이터: view_event_logging + SCREEN
 *
 * 지연 발화 (iOS PR #1528 대응):
 *   → 서버가 should_defer_view_event=true로 내리면 1차 VIEW 발화 보류
 *   → 2차 페이지 도착 후 1차 + 2차 logging을 머지한 payload로 한 번만 발화
 */
export const useLogViewEvent = (data: ScreensReadModel | undefined) => {
  const hasLogged = useRef(false);
  const logAnalyticsEvent = useLogAnalyticsEvent();

  useEffect(() => {
    if (!data || hasLogged.current) return;

    // 지연 발화 플래그 true + 2차 페이지 미도착 + 다음 페이지 존재 → 발화 보류.
    // nextToken이 없는 경우는 유실 방지를 위해 즉시 발화(폴백).
    const isDeferPending =
      data.shouldDeferViewEvent === true &&
      (data.pagesCount ?? 0) < 2 &&
      isNotNil(data.nextToken);
    if (isDeferPending) {
      return;
    }

    hasLogged.current = true;

    const screenName = data.screenName;
    const eventName = screenName
      ? `VIEW_${screenName.toUpperCase()}`
      : 'VIEW';

    // 지연 발화 경로: select에서 머지된 loggingForViewEvent 사용. 그 외는 기존 data.logging 유지.
    const logging =
      data.shouldDeferViewEvent && data.loggingForViewEvent
        ? data.loggingForViewEvent
        : data.logging;

    logAnalyticsEvent({
      name: eventName,
      channel: data.viewEventChannels as LogAnalyticsEventParams['channel'],
      analytics: {
        ...(logging?.analytics as Record<string, unknown>),
        ...(data.viewEventLogging.analytics as Record<string, unknown>),
        SCREEN: screenName,
      },
      ably_only_log: {
        ...(logging?.ably_only_log as Record<string, unknown>),
        ...(data.viewEventLogging.ably_only_log as Record<string, unknown>),
      },
    });
  }, [data, logAnalyticsEvent]);
};